import React, { useState, useEffect, useRef } from 'react';
import {
    Typography,
    Grid,
    TextField,
    Box,
    Divider,
    Stepper,
    Step,
    StepLabel,
    StepButton,
    Paper,
    Chip,
    Button, Snackbar, Alert, Modal
} from '@mui/material';
import {
    Person as PersonIcon,
    Work as WorkIcon,
    AttachMoney as AttachMoneyIcon,
    Policy as PolicyIcon,
    CheckCircle as CheckCircleIcon,
    VerifiedUser as VerifiedUserIcon,
    NoteAdd as NoteAddIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import {axiosInstance, localAxiosToken, localAxiosGet } from '../../../../Axios/axios';
import axios from 'axios';
import TokenExpirePopup from '../../../../components/TokenExpirePopup';
import { useNavigate } from 'react-router-dom';


const rehireVerificationSteps = [
    {
        key: 'personal',
        label: 'Personal & Contact Details',
        tabIndex: 0,
        icon: <PersonIcon />,
        shortLabel: 'Personal'
    },
    {
        key: 'employee',
        label: 'Employment & Job Details',
        tabIndex: 1,
        icon: <WorkIcon />,
        shortLabel: 'Employment'
    },
    {
        key: 'compensation',
        label: 'Compensation & Payroll',
        tabIndex: 2,
        icon: <AttachMoneyIcon />,
        shortLabel: 'Compensation'
    },
    {
        key: 'banking',
        label: 'Banking & Statutory',
        tabIndex: 3,
        icon: <VerifiedUserIcon />,
        shortLabel: 'Banking'
    },
    {
        key: 'documents',
        label: 'Documents & Other',
        tabIndex: 4,
        icon: <NoteAddIcon />,
        shortLabel: 'Documents'
    },
    {
        key: 'policies',
        label: 'Policy Assignment',
        tabIndex: 5,
        icon: <PolicyIcon />,
        shortLabel: 'Policies'
    },
];

const RehireProcess = ({ initialData, onNavigateToTab, userId, onUpdateRehireChecklist, onUpdateRehireDetails, onRehireComplete }) => {
    const rehireChecklist = initialData?.rehireChecklist || {
        personal: false, employee: false, compensation: false,
        policies: false, banking: false, documents: false
    };

    const [activeStep, setActiveStep] = useState(0);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const Token = localStorage.getItem('accessToken')

    // ✅ Use prop for rehire details (always starts empty)
    const rehireDetails = initialData?.rehireDetails || {
        rehireDate: '',
        rehireReason: ''
    };

    const [openTokenExpirePopup, setOpenTokenExpirePopup] = useState(false);
    const [finalExitdata, setFinalExitData] = useState('')
    const navigate = useNavigate()
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success'
    });
    const [loading, setLoading] = useState(false)

    const hasFetchedInitialData = useRef(false);

    // ✅ ONLY fetch exit details (no rehire data prefill)
    useEffect(() => {
        if (!hasFetchedInitialData.current) {
            fetchExitDetailsOnly();
            hasFetchedInitialData.current = true;
        }
    }, []);

    const fetchExitDetailsOnly = async () => {
        if (!userId) return;

        try {
            setLoading(true);
            const token = localStorage.getItem('accessToken');
            const response = await axiosInstance(localAxiosGet(`/hr/getEmployeeExitRehireDetails/${userId}`, token));

            if (response.data && response.data.status === "SUCCESS") {
                const requiredData = response.data.result;
                
                // ✅ ONLY set exit data - NO rehire data prefill
                setFinalExitData(requiredData.exitDetails[0]);

            } else {
                console.error("API success, but exit data is missing.");
                setSnackbar({
                    open: true,
                    message: 'Error in fetching initial Data',
                    severity: 'error'
                });
                setFinalExitData(null);
            }
        } catch (error) {
            console.error("Error fetching employee details:", error);
            if (error.response?.status === 401) {
                setOpenTokenExpirePopup(true);
            } else {
                setSnackbar({
                    open: true,
                    message: 'Failed to load employee exit details',
                    severity: 'error'
                });
            }
            setFinalExitData(null);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const firstIncompleteIndex = rehireVerificationSteps.findIndex(
            step => !rehireChecklist[step.key]
        );

        setActiveStep(prev => {
            const newStep = firstIncompleteIndex !== -1
                ? firstIncompleteIndex
                : rehireVerificationSteps.length - 1;

            return prev !== newStep ? newStep : prev;
        });
    }, [rehireChecklist]);

    const handleRehireChange = (event) => {
        const { name, value } = event.target;

        const updatedDetails = {
            ...rehireDetails,
            [name]: value
        };

        if (onUpdateRehireDetails) {
            onUpdateRehireDetails(updatedDetails);
        }
        
    };

    const handleGoToVerification = (stepIndex) => {
        const step = rehireVerificationSteps[stepIndex];

        if (onNavigateToTab) {
            setActiveStep(stepIndex);

            onNavigateToTab(step.tabIndex, {
                returnToRehire: true,
                verificationStep: step.key,
                stepIndex: stepIndex,
                stepLabel: step.label
            });
        } else {
            setSnackbar({
                open: true,
                message: `Navigation function not provided. Cannot navigate to ${step.label} tab.`,
                severity: 'info'
            });
        }
    };

    const handleStepClick = (stepIndex) => {
        handleGoToVerification(stepIndex);
    };

    const rehireEmployee = async () => {
        try {
            const payload = {
                user_id: userId,
                rehire_date: rehireDetails.rehireDate,
                rehire_reason: rehireDetails.rehireReason
            };

            const response = await axiosInstance(localAxiosToken('/hr/rehireEmployee', payload));

            if (response.data && response.data.status === 'SUCCESS') {
                setSnackbar({
                    open: true,
                    message: response.data.message || 'Employee rehired successfully!',
                    severity: 'success'
                })

            } else {
                setSnackbar({
                    open: true,
                    message: response.data.message || 'Failed to rehire employee',
                    severity: 'error'
                })
            }
        } catch (error) {
            console.error('Rehire API error:', error);
            setSnackbar({
                open: true,
                message: error.message || 'Failed to rehire employee. Please try again.',
                severity: 'error'
            })
        }
    };

    const handleSubmit = async () => {
        const allStepsComplete = Object.values(rehireChecklist).every(isComplete => isComplete);

        if (!rehireDetails.rehireDate || !rehireDetails.rehireReason) {
            setSnackbar({
                open: true,
                message: 'Please fill the Rehire Date and Reason.',
                severity: 'error'
            });
            return;
        }

        if (!allStepsComplete) {
            setSnackbar({
                open: true,
                message: 'Please complete ALL required Verification Steps before confirming Rehire.',
                severity: 'error'
            });
            return;
        }

        await rehireEmployee();

        if (onRehireComplete) {
            onRehireComplete();
        }
    };

    const handleSnackbarClose = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const handleCloseTokenExpirePopup = () => {
        setOpenTokenExpirePopup(false);
        navigate('/');
    };

    return (
        <Box sx={{ mt: 3 }}>
            <Typography variant="h6" gutterBottom>
                Employee Exit Details (Read Only)
            </Typography>

            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={4}>
                    <TextField
                        fullWidth
                        label="Final Date of Exit"
                        disabled
                        value={finalExitdata?.final_date_of_exit || 'N/A'}
                        size="medium"
                    />
                </Grid>
                <Grid item xs={12} sm={8}>
                    <TextField
                        fullWidth
                        label="Exit Reason"
                        disabled
                        value={finalExitdata?.reason_for_exit || 'N/A'}
                        size="medium"
                    />
                </Grid>
            </Grid>

            <Divider sx={{ mb: 3 }} />

            <Typography variant="h6" gutterBottom color="success.main">
                1. Rehire Status & Details
            </Typography>

            <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                    <TextField
                        fullWidth
                        label="Rehire Date"
                        type="date"
                        name="rehireDate"
                        value={rehireDetails.rehireDate || ''}
                        onChange={handleRehireChange}
                        InputLabelProps={{ shrink: true }}
                        size="small"
                        helperText="Employee status becomes ACTIVE from this date."
                    />
                </Grid>

                <Grid item xs={12} sm={8}>
                    <TextField
                        fullWidth
                        label="Reason for Rehire"
                        name="rehireReason"
                        value={rehireDetails.rehireReason || ''}
                        onChange={handleRehireChange}
                        multiline
                        rows={1}
                        helperText="Justify the decision to rehire."
                        size="small"
                    />
                </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" gutterBottom color="primary">
                2. Rehire Data Verification Steps
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Click on each step below to navigate to the respective tab and complete verification
            </Typography>

            <Paper elevation={2} sx={{ p: 3, mb: 3, bgcolor: 'grey.50' }}>
                <Stepper activeStep={activeStep} alternativeLabel>
                    {rehireVerificationSteps.map((step, index) => (
                        <Step key={step.key} completed={rehireChecklist[step.key]}>
                            <StepButton onClick={() => handleStepClick(index)}>
                                <StepLabel
                                    StepIconComponent={() => (
                                        <Box
                                            sx={{
                                                width: 40,
                                                height: 40,
                                                borderRadius: '50%',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                bgcolor: rehireChecklist[step.key]
                                                    ? 'success.main'
                                                    : activeStep === index
                                                        ? 'primary.main'
                                                        : 'grey.300',
                                                color: 'white',
                                                transition: 'all 0.3s',
                                                cursor: 'pointer',
                                                '&:hover': {
                                                    transform: 'scale(1.1)',
                                                }
                                            }}
                                        >
                                            {rehireChecklist[step.key] ? (
                                                <CheckCircleIcon />
                                            ) : (
                                                step.icon
                                            )}
                                        </Box>
                                    )}
                                >
                                    <Typography variant="caption" sx={{ mt: 1, fontWeight: 500 }}>
                                        Step {index + 1}
                                    </Typography>
                                    <Typography variant="caption" display="block" sx={{ fontSize: '0.7rem' }}>
                                        {step.shortLabel}
                                    </Typography>
                                </StepLabel>
                            </StepButton>
                        </Step>
                    ))}
                </Stepper>
            </Paper>

            <Grid container spacing={2}>
                {rehireVerificationSteps.map((step, index) => (
                    <Grid item xs={12} sm={6} key={step.key}>
                        <Paper
                            elevation={1}
                            sx={{
                                p: 2,
                                display: 'flex',
                                alignItems: 'center',
                                border: '2px solid',
                                borderColor: rehireChecklist[step.key] ? 'success.light' : 'grey.300',
                                borderRadius: 2,
                                transition: 'all 0.3s',
                            }}
                        >
                            <Box sx={{ mr: 2, color: rehireChecklist[step.key] ? 'success.main' : 'text.secondary' }}>
                                {step.icon}
                            </Box>
                            <Box sx={{ flexGrow: 1 }}>
                                <Typography variant="body2" fontWeight={500}>
                                    Step {index + 1}: {step.label}
                                </Typography>
                            </Box>
                            <Chip
                                label={rehireChecklist[step.key] ? 'Completed' : 'Pending'}
                                color={rehireChecklist[step.key] ? 'success' : 'warning'}
                                size="small"
                                sx={{ mr: 1 }}
                            />

                            {!rehireChecklist[step.key] && (
                                <Button
                                    size="small"
                                    variant="outlined"
                                    color="warning"
                                    sx={{ mr: 1 }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        const updatedChecklist = {
                                            ...rehireChecklist,
                                            [step.key]: true
                                        };

                                        if (onUpdateRehireChecklist) {
                                            onUpdateRehireChecklist(updatedChecklist);
                                        }

                                        toast.info(`Skipped: ${step.label}`);

                                        const allComplete = Object.values(updatedChecklist).every(val => val === true);
                                        if (allComplete) {
                                            toast.success('All verification steps completed!');
                                        }
                                    }}
                                >
                                    Skip
                                </Button>
                            )}

                            <Button
                                size="small"
                                variant={rehireChecklist[step.key] ? 'outlined' : 'contained'}
                                color={rehireChecklist[step.key] ? 'secondary' : 'primary'}
                                onClick={() => handleGoToVerification(index)}
                            >
                                {rehireChecklist[step.key] ? 'Review' : 'Verify'}
                            </Button>
                        </Paper>
                    </Grid>
                ))}
            </Grid>

            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSubmit}
                >
                    Confirm Rehire
                </Button>
            </Box>

            <Modal
                open={openTokenExpirePopup}
                onClose={handleCloseTokenExpirePopup}
                aria-labelledby="token-expire-modal-title"
                aria-describedby="token-expire-modal-description"
            >
                <TokenExpirePopup onClose={handleCloseTokenExpirePopup} />
            </Modal>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={handleSnackbarClose}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                    variant="filled"
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default RehireProcess;